import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Briefcase, Building, Clock, ArrowRight, Filter, DollarSign, X, FileText, MessageCircle, Lightbulb, GraduationCap, BookOpen, CheckCircle2, Send, UserPlus, Sparkles, Download, ChevronDown } from 'lucide-react';
import { MOCK_JOBS, SPECIALTIES } from '../data/mock';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { formatDate, WA_URL, WHATSAPP_GROUP } from '../lib/utils';
import Reveal from '../components/Reveal';
import { JobBadges } from '../components/JobBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SALARY_RANGES = [
  { label: 'Todas as faixas', value: 'all' },
  { label: 'Até R$ 3.000', value: '0-3000' },
  { label: 'R$ 3.000 a R$ 5.000', value: '3000-5000' },
  { label: 'R$ 5.000 a R$ 8.000', value: '5000-8000' },
  { label: 'R$ 8.000 a R$ 12.000', value: '8000-12000' },
  { label: 'Acima de R$ 12.000', value: '12000-999999' },
];

const CAREER_TIPS = [
  { icon: Lightbulb, title: 'Como montar um currículo de alto impacto', tag: 'Currículo', desc: 'Aprenda a estruturar suas experiências e habilidades para chamar atenção dos recrutadores em poucos segundos.', tips: [
    'Mantenha o currículo em até 2 páginas.',
    'Destaque suas experiências mais recentes.',
    'Evite erros de português e revise antes de enviar.',
    'Informe cursos e certificações relevantes.',
    'Use palavras-chave da vaga para passar nos filtros (ATS).',
    'Salve em PDF com o nome "nome-sobrenome-cv.pdf".',
  ] },
  { icon: GraduationCap, title: 'Entrevista comportamental: o que esperar', tag: 'Entrevista', desc: 'Confira as perguntas mais comuns e como respondê-las usando a técnica STAR para destacar suas competências.', tips: [
    'Pesquise sobre a empresa e a vaga antes da conversa.',
    'Seja objetivo e use exemplos reais (técnica STAR).',
    'Demonstre confiança e mantenha contato visual.',
    'Chegue (ou conecte-se) com antecedência.',
    'Prepare perguntas para fazer ao entrevistador.',
    'Cuide da postura e da comunicação não verbal.',
  ] },
  { icon: BookOpen, title: 'LinkedIn estratégico para conquistar vagas', tag: 'LinkedIn', desc: 'Otimize seu perfil, fortaleça sua rede e use a plataforma como porta de entrada para novas oportunidades.', tips: [
    'Mantenha o perfil sempre atualizado.',
    'Utilize uma foto profissional e um bom título.',
    'Ative o modo "Open to Work" (aberto a oportunidades).',
    'Faça conexões com pessoas da sua área.',
    'Publique e interaja com conteúdos do seu segmento.',
    'Peça recomendações a ex-colegas e gestores.',
  ] },
];

const CV_TIPS = [
  'Inclua um resumo profissional de 3 a 5 linhas no topo do currículo',
  'Descreva resultados quantificáveis (ex: "aumento de 30% nas vendas")',
  'Use palavras-chave da área para passar pelos filtros automáticos (ATS)',
  'Atualize sempre que tiver novas certificações, cursos ou experiências',
  'Salve o arquivo em PDF, com nome no formato "nome-sobrenome-cv.pdf"',
];

const PROCESS_STEPS = [
  { num: '01', title: 'Cadastre seu perfil', desc: 'Crie sua conta gratuitamente, preencha suas informações e faça o upload do seu currículo em PDF.' },
  { num: '02', title: 'Candidate-se às vagas', desc: 'Use os filtros (cargo, região, CEP, salário) para encontrar oportunidades alinhadas ao seu perfil.' },
  { num: '03', title: 'Acompanhe em tempo real', desc: 'Visualize o status de cada candidatura no seu painel: Em análise → Entrevista → Aprovado.' },
  { num: '04', title: 'Receba feedback', desc: 'Nossa equipe entra em contato para avançar nas etapas: entrevistas, testes e proposta final.' },
];

const CandidatosPage = () => {
  const [jobs, setJobs] = useState(MOCK_JOBS);
  const [openTip, setOpenTip] = useState(null);
  const [position, setPosition] = useState('');
  const [region, setRegion] = useState('');
  const [area, setArea] = useState('all');
  const [modality, setModality] = useState('all');
  const [salaryRange, setSalaryRange] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const params = { status: 'open' };
        if (position) params.q = position;
        if (region) params.location = region;
        if (area !== 'all') params.area = area;
        if (salaryRange !== 'all') {
          const [mn, mx] = salaryRange.split('-').map(Number);
          if (mn) params.salary_min = mn;
          if (mx) params.salary_max = mx;
        }
        const res = await axios.get(`${API}/jobs`, { params });
        if (res.data && Array.isArray(res.data)) setJobs(res.data);
      } catch (e) { /* keep mock */ }
    };
    load();
    const interval = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => clearInterval(interval);
  }, [position, region, area, salaryRange]);

  const filtered = jobs.filter((j) => modality === 'all' || j.modality === modality);
  const clearFilters = () => { setPosition(''); setRegion(''); setArea('all'); setModality('all'); setSalaryRange('all'); };
  const hasFilters = position || region || area !== 'all' || modality !== 'all' || salaryRange !== 'all';

  return (
    <div>
      {/* HERO */}
      <section className="hm-gradient text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 text-center max-w-3xl mx-auto relative">
          <Reveal>
            <p className="text-sm uppercase tracking-widest text-white/85 font-bold mb-3">Para Candidatos</p>
            <h1 className="text-4xl md:text-6xl font-extrabold">Sua próxima oportunidade começa aqui</h1>
            <p className="mt-4 text-lg text-white/90">Encontre vagas, cadastre seu currículo no Banco de Talentos, participe do nosso grupo de WhatsApp e acompanhe sua jornada profissional em tempo real.</p>
            <div className="mt-7 flex flex-wrap gap-2 justify-center text-xs font-semibold">
              <a href="#vagas" className="bg-white/15 border border-white/30 hover:bg-white/25 px-4 py-2 rounded-full inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Vagas</a>
              <a href="#cadastro" className="bg-white/15 border border-white/30 hover:bg-white/25 px-4 py-2 rounded-full inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Cadastrar currículo</a>
              <a href="#grupo-whatsapp" className="bg-white/15 border border-white/30 hover:bg-white/25 px-4 py-2 rounded-full inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Grupo WhatsApp</a>
              <a href="#dicas" className="bg-white/15 border border-white/30 hover:bg-white/25 px-4 py-2 rounded-full inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Dicas de carreira</a>
              <a href="#processo" className="bg-white/15 border border-white/30 hover:bg-white/25 px-4 py-2 rounded-full inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Como funciona</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VAGAS EM ABERTO */}
      <section id="vagas" className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
            <div>
              <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Oportunidades abertas</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Vagas em aberto</h2>
              <p className="text-slate-700 mt-1">{filtered.length} vaga(s) disponível(is) <span className="inline-flex items-center gap-1 ml-2 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-semibold"><span className="w-1.5 h-1.5 bg-green-500 rounded-full pulse-dot" /> Atualizado em tempo real</span></p>
            </div>
          </Reveal>

          <Reveal variant="fadeUp" delay={100} className="bg-hm-yellow border border-amber-200 rounded-2xl p-5 mb-6">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Cargo ou palavra-chave..." className="pl-9 h-11 bg-white" />
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Cidade, UF ou CEP..." className="pl-9 h-11 bg-white" />
              </div>
              <Select value={salaryRange} onValueChange={setSalaryRange}>
                <SelectTrigger className="h-11 bg-white"><div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-slate-500" /><SelectValue placeholder="Faixa salarial" /></div></SelectTrigger>
                <SelectContent>
                  {SALARY_RANGES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Área" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as áreas</SelectItem>
                  {SPECIALTIES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={modality} onValueChange={setModality}>
                <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Modalidade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as modalidades</SelectItem>
                  <SelectItem value="Presencial">Presencial</SelectItem>
                  <SelectItem value="Híbrido">Híbrido</SelectItem>
                  <SelectItem value="Remoto">Remoto</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={clearFilters} variant="outline" className="h-11 rounded-md bg-white" disabled={!hasFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar filtros
              </Button>
            </div>
          </Reveal>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Filter className="w-10 h-10 text-slate-400 mx-auto" />
              <p className="mt-3 text-slate-600">Nenhuma vaga encontrada com os filtros aplicados.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {filtered.map((j, idx) => (
                <Reveal key={j.id} variant="fadeUp" delay={Math.min(idx * 60, 300)}>
                  <Link to={`/vaga/${j.id}`} className="block bg-white rounded-2xl border border-slate-100 hover:border-orange-200 p-6 hover-lift">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-hm-orange uppercase tracking-wider">{j.area}</p>
                        <h3 className="text-xl font-bold text-slate-900 mt-1">{j.title}</h3>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-semibold"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Aberta</span>
                    </div>
                    <JobBadges job={j} className="mt-3" />
                    <p className="mt-3 text-sm text-slate-600 line-clamp-2">{j.description}</p>
                    <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.type}</span>
                      <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5" /> {j.modality}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDate(j.created_at || j.createdAt)}</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900">{j.salary}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-hm-orange">Ver detalhes <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CADASTRO + BANCO DE TALENTOS */}
      <section id="cadastro" className="section-padding hm-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center relative">
          <Reveal variant="slideRight">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <FileText className="w-4 h-4" /> Cadastro de currículo
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">Cadastre seu currículo no Banco de Talentos</h2>
            <p className="mt-4 text-white/90 max-w-lg text-lg">Faça parte do nosso banco exclusivo de profissionais qualificados. Empresas parceiras buscam candidatos como você todos os dias.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/portal/candidato/cadastro" className="btn-hm-white">
                <UserPlus className="w-5 h-5" /> Criar conta grátis
              </Link>
              <Link to="/portal/candidato/login" className="bg-white/15 border-2 border-white/40 text-white px-6 py-3.5 rounded-full font-bold hover:bg-white/25 transition-colors inline-flex items-center justify-center gap-2">
                Já tenho conta
              </Link>
            </div>
          </Reveal>
          <Reveal variant="slideLeft" className="bg-white rounded-3xl p-7 text-slate-900 shadow-2xl">
            <h3 className="text-xl font-extrabold">O que você ganha ao se cadastrar:</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                'Acesso prioritário a vagas exclusivas',
                'Acompanhe candidaturas em tempo real',
                'Receba alertas personalizados por área',
                'Upload de currículo em PDF (até 5MB)',
                'Visibilidade para recrutadores parceiros',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-slate-100 text-sm text-slate-600">
              <strong className="text-hm-orange">+850 profissionais</strong> já foram contratados via HM Talent.
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESSO DE CANDIDATURA */}
      <section id="processo" className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Como funciona</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Processo de candidatura em 4 passos</h2>
            <p className="mt-3 text-slate-700">Simples, rápido e transparente. Acompanhe cada etapa em tempo real.</p>
          </Reveal>
          <div className="grid md:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((p, i) => (
              <Reveal key={i} delay={i * 100} variant="fadeUp" className={`p-7 rounded-2xl hover-lift relative ${i % 2 === 0 ? 'bg-hm-yellow border border-amber-100' : 'hm-gradient text-white'}`}>
                <div className={`text-3xl font-extrabold mb-3 ${i % 2 === 0 ? 'text-hm-orange' : 'text-white'}`}>{p.num}</div>
                <h3 className={`text-lg font-bold ${i % 2 === 0 ? 'text-slate-900' : 'text-white'}`}>{p.title}</h3>
                <p className={`text-sm leading-relaxed mt-2 ${i % 2 === 0 ? 'text-slate-700' : 'text-white/90'}`}>{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ORIENTAÇÕES DE CURRÍCULO */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Boas práticas</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Orientações para um currículo de destaque</h2>
            <p className="mt-3 text-slate-700">Aplique estas dicas para aumentar suas chances de ser convocado para entrevistas.</p>
            <div className="mt-6 space-y-3">
              {CV_TIPS.map((t, i) => (
                <Reveal key={i} variant="slideRight" delay={i * 80} className="flex items-start gap-3 bg-white border border-amber-100 rounded-xl p-4 hover-lift">
                  <div className="w-8 h-8 rounded-lg hm-gradient flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm text-slate-700">{t}</p>
                </Reveal>
              ))}
            </div>
          </Reveal>
          <Reveal variant="slideLeft" className="hm-gradient text-white rounded-3xl p-8 shadow-xl">
            <FileText className="w-12 h-12" />
            <h3 className="text-2xl font-extrabold mt-4">Currículo modelo HM Talent</h3>
            <p className="text-white/90 mt-2 leading-relaxed">Baixe o modelo de currículo da HM Talent e organize suas informações no formato que valorizamos na seleção. É só preencher e enviar com a sua candidatura.</p>
            <a href={`${API}/modelo-curriculo`} target="_blank" rel="noopener noreferrer" download data-testid="download-modelo-curriculo" className="btn-hm-white mt-5">
              <Download className="w-5 h-5" /> Baixar Currículo Modelo HM Talent
            </a>
          </Reveal>
        </div>
      </section>

      {/* DICAS DE CARREIRA */}
      <section id="dicas" className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Conteúdo exclusivo</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Dicas de Carreira</h2>
            <p className="mt-3 text-slate-700">Aprenda com nossos especialistas e dê o próximo passo na sua trajetória profissional.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {CAREER_TIPS.map((t, i) => (
              <Reveal key={i} delay={i * 100} variant="fadeUp" className={`group rounded-2xl overflow-hidden hover-lift ${i === 1 ? 'hm-gradient text-white' : 'bg-hm-yellow border border-amber-100'}`}>
                <div className="p-7">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${i === 1 ? 'bg-white/20' : 'hm-gradient'}`}>
                    <t.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className={`text-xs uppercase tracking-wider font-bold ${i === 1 ? 'text-white/85' : 'text-hm-orange'}`}>{t.tag}</span>
                  <h3 className={`text-xl font-bold mt-2 ${i === 1 ? 'text-white' : 'text-slate-900'}`}>{t.title}</h3>
                  <p className={`text-sm mt-2 leading-relaxed ${i === 1 ? 'text-white/90' : 'text-slate-700'}`}>{t.desc}</p>
                  <button
                    type="button"
                    data-testid={`career-tip-toggle-${i}`}
                    onClick={() => setOpenTip(openTip === i ? null : i)}
                    aria-expanded={openTip === i}
                    className={`mt-5 inline-flex items-center gap-1 text-sm font-semibold transition-all ${i === 1 ? 'text-white' : 'text-hm-orange'}`}
                  >
                    {openTip === i ? 'Recolher' : 'Ler dica'}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openTip === i ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    data-testid={`career-tip-content-${i}`}
                    className={`grid transition-all duration-300 ease-out ${openTip === i ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}
                  >
                    <ul className={`overflow-hidden space-y-2 ${i === 1 ? 'text-white/95' : 'text-slate-700'}`}>
                      {t.tips.map((tip, k) => (
                        <li key={k} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${i === 1 ? 'text-white' : 'text-hm-orange'}`} />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GRUPO WHATSAPP */}
      <section id="grupo-whatsapp" className="section-padding hm-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm relative">
          <Reveal className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 px-4 py-1.5 rounded-full text-sm font-semibold mb-5">
              <MessageCircle className="w-4 h-4" /> Grupo oficial de vagas
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold">Entre no nosso Grupo de WhatsApp</h2>
            <p className="mt-4 text-white/95 text-lg">Entre no nosso grupo exclusivo de vagas e receba oportunidades em primeira mão. Conteúdos de carreira, eventos e networking com nossos especialistas em recrutamento.</p>
            <a href={WHATSAPP_GROUP} target="_blank" rel="noopener noreferrer" className="mt-8 btn-hm-white !px-8 !py-4 !text-lg">
              <MessageCircle className="w-6 h-6" /> Entrar no grupo agora
            </a>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-white/95">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Vagas em primeira mão</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Conteúdo gratuito</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Networking ativo</span>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CandidatosPage;
